import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, campaignsAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import SearchInput from '../components/SearchInput';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { LeadsIcon } from '../components/Icons';
import './Leads.css';

export default function Leads() {
  const navigate = useNavigate();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [pausedFilter, setPausedFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [leadCampaignMap, setLeadCampaignMap] = useState({}); // Map of lead_id -> [campaign names]
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, campaignFilter, priorityFilter, pausedFilter, leads, leadCampaignMap]);

  const loadData = async () => {
    await Promise.all([loadLeads(), loadCampaignAssignments(), loadCampaigns()]);
  };

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await leadsAPI.getAll();
      // Handle paginated response
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setLeads(data);
    } catch (error) {
      console.error('Failed to load leads:', error);
      if (error.response?.status === 403) {
        toast.warning('Authentication required. Please login.');
      } else {
        toast.error('Failed to load leads. Check console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await campaignsAPI.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadCampaignAssignments = async () => {
    try {
      const response = await campaignsAPI.getLeadAssignments();
      const assignments = response.data?.lead_assignments || {};
      const leadToCampaigns = {};
      
      for (const [leadId, campaignList] of Object.entries(assignments)) {
        leadToCampaigns[leadId] = campaignList.map(c => ({
          id: c.campaign_id,
          name: c.campaign_name,
          active: c.active
        }));
      }
      setLeadCampaignMap(leadToCampaigns);
    } catch (error) {
      console.error('Failed to load campaign assignments:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...leads];

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Campaign filter
    if (campaignFilter) {
      filtered = filtered.filter(lead => {
        const campaigns = leadCampaignMap[lead.id] || [];
        return campaigns.some(c => c.id === campaignFilter);
      });
    }

    // Priority filter
    if (priorityFilter) {
      if (priorityFilter === 'high') {
        filtered = filtered.filter(lead => (lead.priority_score || 0) >= 7);
      } else if (priorityFilter === 'medium') {
        filtered = filtered.filter(lead => {
          const score = lead.priority_score || 0;
          return score >= 4 && score < 7;
        });
      } else if (priorityFilter === 'low') {
        filtered = filtered.filter(lead => (lead.priority_score || 0) < 4);
      }
    }

    // Paused filter
    if (pausedFilter === 'paused') {
      // A lead is paused if next_action_date is far in the future (100+ years)
      filtered = filtered.filter(lead => {
        if (!lead.next_action_date) return false;
        const actionDate = new Date(lead.next_action_date);
        const now = new Date();
        const yearsDiff = (actionDate - now) / (1000 * 60 * 60 * 24 * 365);
        return yearsDiff > 50; // Far future = paused
      });
    } else if (pausedFilter === 'active') {
      filtered = filtered.filter(lead => {
        if (!lead.next_action_date) return true; // New leads are active
        const actionDate = new Date(lead.next_action_date);
        const now = new Date();
        const yearsDiff = (actionDate - now) / (1000 * 60 * 60 * 24 * 365);
        return yearsDiff <= 50; // Not far future = active
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.email?.toLowerCase().includes(query) ||
        lead.first_name?.toLowerCase().includes(query) ||
        lead.last_name?.toLowerCase().includes(query) ||
        lead.phone?.toLowerCase().includes(query)
      );
    }

    setFilteredLeads(filtered);
  };

  const pauseLead = async (id) => {
    if (!window.confirm('Pause this lead? Outreach will stop in all campaigns.')) {
      return;
    }

    try {
      setActionLoading(`pause-${id}`);
      await leadsAPI.pause(id);
      toast.success('Lead paused successfully - will not be processed in any campaigns');
      await loadData(); // Reload all data to reflect changes
    } catch (error) {
      console.error('Failed to pause lead:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to pause lead';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const resumeLead = async (id) => {
    if (!window.confirm('Resume this lead? Outreach will continue in assigned campaigns.')) {
      return;
    }

    try {
      setActionLoading(`resume-${id}`);
      await leadsAPI.resume(id);
      toast.success('Lead resumed successfully - will be processed in campaigns');
      await loadData(); // Reload all data to reflect changes
    } catch (error) {
      console.error('Failed to resume lead:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to resume lead';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleImportLeads = async () => {
    if (!window.confirm('Import leads from Google Sheets? This will process all rows in the Leads_Import sheet.')) {
      return;
    }

    try {
      setImportLoading(true);
      const response = await leadsAPI.importFromSheets({
        dry_run: false
      });
      
      const results = response.data.results || {};
      const message = response.data.message || 'Import completed';
      
      toast.success(`${message}. Check console for details.`);
      console.log('Import results:', results);
      
      // Reload leads after import
      await loadData();
    } catch (error) {
      console.error('Failed to import leads:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.detail || 
                      'Failed to import leads. Check console for details.';
      toast.error(errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadData();
    toast.success('Leads data refreshed');
  };

  const isPaused = (lead) => {
    if (!lead.next_action_date) return false;
    const actionDate = new Date(lead.next_action_date);
    const now = new Date();
    const yearsDiff = (actionDate - now) / (1000 * 60 * 60 * 24 * 365);
    return yearsDiff > 50; // Far future = paused
  };

  const getLeadCampaigns = (leadId) => {
    return leadCampaignMap[leadId] || [];
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Leads</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={7} />
        </Card>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">Manage your lead database</p>
        </div>
        <div className="page-actions">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleRefresh}
            loading={loading}
            title="Refresh lead data"
          >
            ↻ Refresh
          </Button>
          <Button 
            variant="primary" 
            onClick={handleImportLeads}
            loading={importLoading}
          >
            Import from Google Sheets
          </Button>
        </div>
      </div>

      <Card>
        <div className="filters-section">
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="CONTACTED">Contacted</option>
              <option value="ENGAGED">Engaged</option>
              <option value="REPLIED">Replied</option>
              <option value="BOOKED">Booked</option>
              <option value="NOT_INTERESTED">Not Interested</option>
              <option value="DEAD">Dead</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Campaign</label>
            <select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Campaigns</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} {campaign.active ? '(Active)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Priority</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Priorities</option>
              <option value="high">High (7+)</option>
              <option value="medium">Medium (4-6)</option>
              <option value="low">Low (0-3)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              value={pausedFilter}
              onChange={(e) => setPausedFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Leads</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div className="filter-group" style={{ flex: 1, maxWidth: '400px' }}>
            <label className="filter-label">Search</label>
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or phone..."
            />
          </div>
        </div>
      </Card>

      {filteredLeads.length === 0 ? (
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <LeadsIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">
              {searchQuery || statusFilter || campaignFilter || priorityFilter || pausedFilter ? 'No leads found' : 'No leads yet'}
            </div>
            <div className="empty-state-description">
              {searchQuery || statusFilter || campaignFilter || priorityFilter || pausedFilter
                ? 'Try adjusting your filters'
                : 'Import leads from Google Sheets or add them manually'}
            </div>
          </div>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Facebook ID</th>
                  <th>Instagram ID</th>
                  <th>Campaigns</th>
                  <th>Next Action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => {
                  const leadCampaigns = getLeadCampaigns(lead.id);
                  const paused = isPaused(lead);
                  
                  return (
                    <tr key={lead.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/leads/${lead.id}`)}>
                      <td>
                        <div className="lead-name">
                          <strong style={{ color: 'var(--primary)' }}>{lead.first_name} {lead.last_name}</strong>
                          {paused && (
                            <span 
                              style={{ 
                                marginLeft: '8px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--warning)',
                                fontWeight: 'var(--font-weight-semibold)'
                              }}
                              title="This lead is paused"
                            >
                              ⏸ Paused
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary">{lead.email}</span>
                      </td>
                      <td>
                        <span className={`status-badge status-${lead.status?.toLowerCase()}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{
                          background: (lead.priority_score || 0) >= 7 ? 'rgba(220, 38, 38, 0.15)' :
                                     (lead.priority_score || 0) >= 4 ? 'rgba(251, 191, 36, 0.15)' :
                                     'rgba(107, 114, 128, 0.15)',
                          color: (lead.priority_score || 0) >= 7 ? 'var(--error)' :
                                 (lead.priority_score || 0) >= 4 ? 'var(--warning)' :
                                 'var(--text-secondary)'
                        }}>
                          {lead.priority_score || 0}
                        </span>
                      </td>
                      <td>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {lead.facebook_user_id ? (
                            <span title={`Facebook ID: ${lead.facebook_user_id}`}>
                              {lead.facebook_user_id.substring(0, 15)}...
                            </span>
                          ) : (
                            '-'
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {lead.instagram_user_id ? (
                            <span title={`Instagram ID: ${lead.instagram_user_id}`}>
                              {lead.instagram_user_id.substring(0, 15)}...
                            </span>
                          ) : (
                            '-'
                          )}
                        </span>
                      </td>
                      <td>
                        {leadCampaigns.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {leadCampaigns.map(campaign => (
                              <span 
                                key={campaign.id}
                                style={{ 
                                  fontSize: 'var(--font-size-xs)',
                                  padding: '2px 6px',
                                  borderRadius: 'var(--border-radius)',
                                  background: campaign.active ? 'rgba(5, 150, 105, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                                  color: campaign.active ? 'var(--success)' : 'var(--text-secondary)',
                                  display: 'inline-block',
                                  width: 'fit-content'
                                }}
                                title={campaign.active ? 'Active campaign' : 'Inactive campaign'}
                              >
                                {campaign.name} {campaign.active ? '✓' : ''}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            None
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {lead.next_action_date
                            ? (paused ? 'Paused' : new Date(lead.next_action_date).toLocaleString())
                            : 'N/A'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="table-actions">
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => pauseLead(lead.id)}
                            loading={actionLoading === `pause-${lead.id}`}
                            disabled={lead.status === 'DEAD' || lead.status === 'NOT_INTERESTED' || paused}
                            title={paused ? 'Lead is already paused' : 'Pause this lead in all campaigns'}
                          >
                            Pause
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => resumeLead(lead.id)}
                            loading={actionLoading === `resume-${lead.id}`}
                            disabled={!paused}
                            title={!paused ? 'Lead is already active' : 'Resume this lead in all campaigns'}
                          >
                            Resume
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
